import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { GroupReportsService } from '../services/group-reports.service';
import { GroupTemplateActions } from './groupTemplate.actions';
import { mergeMap, of } from 'rxjs';
import { catchError, map, tap, switchMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

@Injectable()
export class GroupTemplateEffects {
  loadTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupTemplateActions.loadTemplate),
      mergeMap(({ templateOid }) =>
        this.groupReportService.loadGroupTemplate(templateOid).pipe(
          map((resultResponse) => {
            let template = null;
            if (resultResponse.success && resultResponse.data.length) {
              template = resultResponse.data[0];
            }
            return GroupTemplateActions.loadTemplateSuccess({ template });
          }),
          catchError((error) => of(GroupTemplateActions.loadTemplateFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  saveTemplateBuilder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupTemplateActions.saveTemplateBuilder),
      mergeMap(({ templateOid, template, isNew, redirectToList }) => {
        if (isNew) {
          // For new templates: first create in DB, then save with data
          return this.groupReportService.createGroupTemplate().pipe(
            switchMap((createResponse) => {
              if (!createResponse.success || !createResponse.data?.length) {
                return of(
                  GroupTemplateActions.saveTemplateBuilderFailure({
                    error: createResponse.errorText || 'Failed to create configuration',
                  }),
                );
              }

              const newTemplateOid = createResponse.data[0].idobject;
              const templateToSave = { ...template, idobject: newTemplateOid };

              return this.groupReportService.saveGroupTemplate(newTemplateOid, templateToSave).pipe(
                switchMap((saveResponse) => {
                  if (!saveResponse.success) {
                    return of(
                      GroupTemplateActions.saveTemplateBuilderFailure({ error: 'Failed to save configuration' }),
                    );
                  }

                  if (!template?.surveyVersionOid) {
                    return of(
                      GroupTemplateActions.saveTemplateBuilderSuccess({ templateOid: newTemplateOid, redirectToList }),
                    );
                  }

                  return this.groupReportService.fillGroupTemplateData(newTemplateOid).pipe(
                    map((fillResponse) => {
                      if (fillResponse.success) {
                        return GroupTemplateActions.saveTemplateBuilderSuccess({
                          templateOid: newTemplateOid,
                          redirectToList,
                        });
                      }
                      return GroupTemplateActions.saveTemplateBuilderFailure({
                        error: 'Failed to fill configuration data',
                      });
                    }),
                    catchError((error) =>
                      of(GroupTemplateActions.saveTemplateBuilderFailure({ error: error.message })),
                    ),
                  );
                }),
                catchError((error) => of(GroupTemplateActions.saveTemplateBuilderFailure({ error: error.message }))),
              );
            }),
            catchError((error) => of(GroupTemplateActions.saveTemplateBuilderFailure({ error: error.message }))),
          );
        }

        // For existing templates: just save
        return this.groupReportService.saveGroupTemplate(templateOid, template).pipe(
          mergeMap((resultResponse) => {
            if (!resultResponse.success) {
              return of(GroupTemplateActions.saveTemplateBuilderFailure({ error: 'Failed to save configuration' }));
            }

            if (!template?.surveyVersionOid) {
              return of(GroupTemplateActions.saveTemplateBuilderSuccess({ templateOid, redirectToList }));
            }

            return this.groupReportService.fillGroupTemplateData(templateOid).pipe(
              map((fillResponse) => {
                if (fillResponse.success) {
                  return GroupTemplateActions.saveTemplateBuilderSuccess({ templateOid, redirectToList });
                }
                return GroupTemplateActions.saveTemplateBuilderFailure({ error: 'Failed to fill configuration data' });
              }),
              catchError((error) => of(GroupTemplateActions.saveTemplateBuilderFailure({ error: error.message }))),
            );
          }),
          catchError((error) => of(GroupTemplateActions.saveTemplateBuilderFailure({ error: error.message }))),
        );
      }),
    ),
  );

  navigateAfterSave$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GroupTemplateActions.saveTemplateBuilderSuccess),
        tap(({ redirectToList }) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Saved',
            detail: 'Configuration saved',
          });
          if (redirectToList) {
            setTimeout(() => {
              this.router.navigate(['/group-reports']);
            }, 100);
          }
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private groupReportService: GroupReportsService,
    private router: Router,
    private messageService: MessageService,
  ) {}
}
